import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../utils/supabaseClient';
import styles from './Profile.module.css';
import { 
  FiUser, FiMail, FiPhone, FiBriefcase, 
  FiCalendar, FiLogOut, FiEdit2, FiSave,
  FiX, FiCheckCircle, FiActivity, FiClock,
  FiMessageSquare, FiHome, FiBox, FiBookOpen,
  FiInfo, FiGrid, FiStar, FiMail as FiMailIcon,
  FiTrendingUp, FiRefreshCw
} from 'react-icons/fi';

const Profile = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const [activities, setActivities] = useState([]);
  const [activityStats, setActivityStats] = useState({
    total: 0,
    lastWeek: 0,
    mostVisited: '',
    lastActive: null
  });
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    role: ''
  });
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    fetchUserAndProfile();
    fetchUserActivities();
    // Track page view with a small delay to ensure user is loaded
    setTimeout(() => {
      trackPageView('Profile Page');
    }, 1000);
  }, []);

  // Function to track user activity with better error handling
  const trackActivity = async (activityType, description, pageName = null) => {
    try {
      console.log('📝 Tracking activity:', { activityType, description, pageName });
      
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        console.log('❌ No session found, cannot track activity');
        return;
      }
      
      console.log('✅ User ID:', session.user.id);
      
      const activityData = {
        user_id: session.user.id,
        activity_type: activityType,
        description: description,
        page_name: pageName,
        created_at: new Date().toISOString()
      };
      
      console.log('📤 Inserting activity:', activityData);
      
      const { data, error } = await supabase
        .from('user_activity')
        .insert([activityData])
        .select();
      
      if (error) {
        console.error('❌ Error tracking activity:', error);
        console.error('Error details:', JSON.stringify(error, null, 2));
        return;
      }
      
      console.log('✅ Activity tracked successfully:', data);
      return data;
    } catch (error) {
      console.error('❌ Exception in trackActivity:', error);
    }
  };

  // Track page view
  const trackPageView = (pageName) => {
    trackActivity('page_view', `Viewed ${pageName}`, pageName);
  };

  const fetchUserAndProfile = async () => {
    try {
      setLoading(true);
      
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        console.error('No session found:', sessionError);
        navigate('/login');
        return;
      }
      
      setUser(session.user);

      const { data: profileData, error: profileError } = await supabase
        .from('user_profiles')
        .select('*')
        .eq('id', session.user.id)
        .single();

      if (profileError && profileError.code !== 'PGRST116') {
        console.error('Error fetching profile:', profileError);
      }

      if (profileData) {
        setProfile(profileData);
        setFormData({
          name: profileData.name || '',
          phone: profileData.phone || '',
          role: profileData.role || ''
        });
      } else {
        const newProfile = {
          id: session.user.id,
          email: session.user.email,
          name: session.user.user_metadata?.full_name || '',
          phone: '',
          role: '',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString()
        };
        
        const { error: insertError } = await supabase
          .from('user_profiles')
          .insert([newProfile]);
          
        if (insertError) {
          console.error('Error creating profile:', insertError);
        } else {
          setProfile(newProfile);
        }
      }
    } catch (error) {
      console.error('Error in fetchUserAndProfile:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchUserActivities = async () => {
    try {
      console.log('📥 Fetching user activities...');
      
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        console.log('No session found');
        return;
      }
      
      console.log('Fetching activities for user:', session.user.id);
      
      const { data: activitiesData, error } = await supabase
        .from('user_activity')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        console.error('Error fetching activities:', error);
        return;
      }

      console.log(`✅ Fetched ${activitiesData?.length || 0} activities`);
      setActivities(activitiesData || []);

      // Calculate statistics
      if (activitiesData && activitiesData.length > 0) {
        const lastWeek = activitiesData.filter(a => {
          const date = new Date(a.created_at);
          const weekAgo = new Date();
          weekAgo.setDate(weekAgo.getDate() - 7);
          return date >= weekAgo;
        }).length;

        // Most visited page
        const pageCounts = {};
        activitiesData.forEach(a => {
          if (a.page_name) {
            pageCounts[a.page_name] = (pageCounts[a.page_name] || 0) + 1;
          }
        });
        const mostVisited = Object.keys(pageCounts).reduce((a, b) => 
          pageCounts[a] > pageCounts[b] ? a : b, '');

        setActivityStats({
          total: activitiesData.length,
          lastWeek: lastWeek,
          mostVisited: mostVisited || 'None',
          lastActive: activitiesData[0]?.created_at
        });
      }
    } catch (error) {
      console.error('Error fetching activities:', error);
    }
  };

  const handleUpdateProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setMessage({ type: '', text: '' });

    try {
      const { error } = await supabase
        .from('user_profiles')
        .upsert({
          id: user.id,
          name: formData.name,
          phone: formData.phone,
          role: formData.role,
          email: user.email,
          updated_at: new Date().toISOString()
        });

      if (error) throw error;

      await trackActivity('profile_update', 'Updated profile information');
      setMessage({ type: 'success', text: 'Profile updated successfully!' });
      setEditing(false);
      
      setProfile({
        ...profile,
        name: formData.name,
        phone: formData.phone,
        role: formData.role
      });
      
      // Refresh activities after update
      await fetchUserActivities();
      
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      setMessage({ type: 'error', text: error.message });
    } finally {
      setSaving(false);
    }
  };

  const handleLogout = async () => {
    try {
      await trackActivity('logout', 'User logged out');
      await supabase.auth.signOut();
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/';
    } catch (error) {
      console.error('Logout error:', error);
      window.location.href = '/';
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const getActivityIcon = (activityType) => {
    switch(activityType) {
      case 'page_view':
        return <FiActivity />;
      case 'profile_update':
        return <FiEdit2 />;
      case 'login':
        return <FiLogOut />;
      case 'logout':
        return <FiLogOut />;
      default:
        return <FiActivity />;
    }
  };

  const getActivityColor = (activityType) => {
    switch(activityType) {
      case 'page_view':
        return '#3b82f6';
      case 'profile_update':
        return '#10b981';
      case 'login':
        return '#8b5cf6';
      case 'logout':
        return '#ef4444';
      default:
        return '#B08B35';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? '' : 's'} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    return date.toLocaleDateString();
  };

  if (loading) {
    return (
      <div className={styles.loadingContainer}>
        <div className={styles.spinner}></div>
        <p>Loading profile...</p>
      </div>
    );
  }

  return (
    <div className={styles.profilePage}>
      <div className={styles.container}>
        <div className={styles.profileHeader}>
          <h1>My Profile</h1>
          {!editing ? (
            <button onClick={() => setEditing(true)} className={styles.editButton}>
              <FiEdit2 /> Edit Profile
            </button>
          ) : (
            <button onClick={() => setEditing(false)} className={styles.cancelButton}>
              <FiX /> Cancel
            </button>
          )}
        </div>

        {message.text && (
          <div className={`${styles.message} ${styles[message.type]}`}>
            {message.type === 'success' && <FiCheckCircle />}
            <span>{message.text}</span>
          </div>
        )}

        <div className={styles.twoColumnLayout}>
          {/* Left Column - Profile Information */}
          <div className={styles.leftColumn}>
            <div className={styles.profileCard}>
              <h2>Personal Information</h2>
              
              {!editing ? (
                <div className={styles.profileInfo}>
                  <div className={styles.infoRow}>
                    <FiUser className={styles.infoIcon} />
                    <div>
                      <label>Full Name</label>
                      <p>{profile?.name || user?.user_metadata?.full_name || 'Not provided'}</p>
                    </div>
                  </div>
                  <div className={styles.infoRow}>
                    <FiMail className={styles.infoIcon} />
                    <div>
                      <label>Email Address</label>
                      <p>{user?.email}</p>
                    </div>
                  </div>
                  <div className={styles.infoRow}>
                    <FiPhone className={styles.infoIcon} />
                    <div>
                      <label>Phone Number</label>
                      <p>{profile?.phone || 'Not provided'}</p>
                    </div>
                  </div>
                  <div className={styles.infoRow}>
                    <FiBriefcase className={styles.infoIcon} />
                    <div>
                      <label>Role</label>
                      <p>{profile?.role || 'Not specified'}</p>
                    </div>
                  </div>
                  <div className={styles.infoRow}>
                    <FiCalendar className={styles.infoIcon} />
                    <div>
                      <label>Member Since</label>
                      <p>{new Date(user?.created_at).toLocaleDateString()}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <form onSubmit={handleUpdateProfile} className={styles.editForm}>
                  <div className={styles.formGroup}>
                    <label>Full Name</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="Enter your full name"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Email Address</label>
                    <input
                      type="email"
                      value={user?.email}
                      disabled
                      className={styles.disabledInput}
                    />
                    <small>Email cannot be changed</small>
                  </div>
                  <div className={styles.formGroup}>
                    <label>Phone Number</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="Enter your phone number"
                    />
                  </div>
                  <div className={styles.formGroup}>
                    <label>Role</label>
                    <select name="role" value={formData.role} onChange={handleChange}>
                      <option value="">Select your role</option>
                      <option value="Student">Student</option>
                      <option value="IT Professional">IT Professional</option>
                      <option value="Business Owner">Business Owner</option>
                      <option value="Entrepreneur">Entrepreneur</option>
                      <option value="Startup Founder">Startup Founder</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className={styles.formActions}>
                    <button type="submit" className={styles.saveButton} disabled={saving}>
                      <FiSave /> {saving ? 'Saving...' : 'Save Changes'}
                    </button>
                  </div>
                </form>
              )}
            </div>

            <div className={styles.logoutSection}>
              <button onClick={handleLogout} className={styles.logoutButton}>
                <FiLogOut /> Sign Out
              </button>
            </div>
          </div>

          {/* Right Column - Activity Section */}
          <div className={styles.rightColumn}>
            <div className={styles.activityStatsCard}>
              <h2>
                <FiTrendingUp /> Activity Overview
              </h2>
              <div className={styles.statsGrid}>
                <div className={styles.statItem}>
                  <div className={styles.statValue}>{activityStats.total}</div>
                  <div className={styles.statLabel}>Total Activities</div>
                </div>
                <div className={styles.statItem}>
                  <div className={styles.statValue}>{activityStats.lastWeek}</div>
                  <div className={styles.statLabel}>Last 7 Days</div>
                </div>
                <div className={styles.statItem}>
                  <div className={styles.statValue}>{activityStats.mostVisited}</div>
                  <div className={styles.statLabel}>Most Visited</div>
                </div>
                <div className={styles.statItem}>
                  <div className={styles.statValue}>
                    {activityStats.lastActive ? formatDate(activityStats.lastActive) : 'Never'}
                  </div>
                  <div className={styles.statLabel}>Last Active</div>
                </div>
              </div>
            </div>

            <div className={styles.activityCard}>
              <h2>
                <FiActivity /> Recent Activity
              </h2>
              {activities.length === 0 ? (
                <div className={styles.noActivity}>
                  <FiActivity />
                  <p>No recent activity</p>
                  <small>Your activities will appear here as you use the website</small>
                </div>
              ) : (
                <div className={styles.activityList}>
                  {activities.map((activity, index) => (
                    <div key={index} className={styles.activityItem}>
                      <div 
                        className={styles.activityIcon}
                        style={{ background: `${getActivityColor(activity.activity_type)}20`, color: getActivityColor(activity.activity_type) }}
                      >
                        {getActivityIcon(activity.activity_type)}
                      </div>
                      <div className={styles.activityDetails}>
                        <p className={styles.activityDescription}>{activity.description}</p>
                        <div className={styles.activityMeta}>
                          <span className={styles.activityTime}>
                            <FiClock /> {formatDate(activity.created_at)}
                          </span>
                          {activity.page_name && (
                            <span className={styles.activityPage}>
                              {activity.page_name}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;